import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Card,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Nota,
  PageHeader,
  Skeleton,
} from '../../../ui';

import ModalConfirmarExpurgo from '../../../components/modal/expurgo/ModalConfirmarExpurgo';

import { TI } from '../routes';
import {
  HistoricoExpurgos,
  PreviaExpurgo,
  RegistroExpurgo,
  ResultadoExpurgo,
  buscarPreviaExpurgo,
  expurgarAssociado,
  liberarDaLista,
  listarExpurgos,
} from '../../../api/associado-expurgo/associadoExpurgoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataHora } from '../../../utils/data';

/**
 * Exclusão definitiva de associado (at-77, item 64) — `/ti/expurgo`, **só ADMIN**.
 *
 * É a **única** tela do SAAC que apaga de verdade. Todo o resto do sistema marca:
 * `deletedAt`, `situacao = INATIVO`, `SUBSTITUIDO`. Ela existe porque o soft delete
 * não resolve o caso do dado de teste — `cpfCnpj`, `rg`, `placa`, `chassi` e
 * `renavam` são únicos, então o registro marcado **continua bloqueando o
 * recadastro** daquele CPF e daquela placa.
 *
 * 🔴 **Duas etapas, em rotas diferentes, de propósito:** a prévia mostra o
 * inventário exato e **não apaga nada**; a execução é outro clique, com motivo e a
 * digitação do CPF. Ninguém deve descobrir o que vai sair no mesmo clique que apaga.
 *
 * ⚠️ Os avisos e a mensagem final vêm **prontos do backend** (convenção
 * at-24/at-32/at-47): a regra de o que é grave mora num lugar só.
 */

/** Como as contagens aparecem — rótulo humano por chave do inventário. */
const LINHAS_DO_INVENTARIO: Array<{ chave: keyof PreviaExpurgo['inventario']; rotulo: string }> = [
  { chave: 'veiculos', rotulo: 'Veículos' },
  { chave: 'fotosDeDocumento', rotulo: 'Fotos de documento' },
  { chave: 'vistorias', rotulo: 'Vistorias' },
  { chave: 'fotosDeVistoria', rotulo: 'Fotos de vistoria' },
  { chave: 'contratos', rotulo: 'Contratos' },
  { chave: 'termosLocais', rotulo: 'Termos gerados' },
  { chave: 'assinaturas', rotulo: 'Assinaturas' },
  { chave: 'produtosVinculados', rotulo: 'Produtos vinculados' },
  { chave: 'leadsDeCotacao', rotulo: 'Leads de cotação' },
  { chave: 'cotacoesGeradas', rotulo: 'Cotações geradas' },
  { chave: 'rascunhosDeAdesao', rotulo: 'Rascunhos de adesão' },
  { chave: 'divergencias', rotulo: 'Divergências com o SGA' },
  { chave: 'arquivosNoStorage', rotulo: 'Arquivos no armazenamento' },
];

const ExpurgoAssociadoPage: React.FC = () => {
  const voltar = useVoltar(TI());

  const [cpf, setCpf] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);
  const [previa, setPrevia] = useState<PreviaExpurgo | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [apagando, setApagando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoExpurgo | null>(null);

  const [historico, setHistorico] = useState<HistoricoExpurgos | null>(null);
  const [erroHistorico, setErroHistorico] = useState<string | null>(null);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    setErroHistorico(null);
    try {
      setHistorico(await listarExpurgos(1, 20));
    } catch (erro: any) {
      // ⚠️ Falha de leitura NÃO pode virar "nunca ninguém foi apagado" — são duas
      // conclusões opostas sobre o sistema (lição da at-69).
      setErroHistorico(erro?.message ?? 'Não foi possível carregar o histórico.');
      setHistorico(null);
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  const buscar = async (evento?: React.FormEvent) => {
    evento?.preventDefault();

    const digitos = cpf.replace(/\D/g, '');
    if (digitos.length !== 11 && digitos.length !== 14) {
      setErroBusca('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).');
      setPrevia(null);
      return;
    }

    setBuscando(true);
    setErroBusca(null);
    setPrevia(null);
    setResultado(null);

    try {
      setPrevia(await buscarPreviaExpurgo(digitos));
    } catch (erro: any) {
      setErroBusca(erro?.message ?? 'Não foi possível buscar este CPF.');
    } finally {
      setBuscando(false);
    }
  };

  const confirmarExpurgo = async (dados: {
    motivo: string;
    marcarTesteNoSga: boolean;
  }) => {
    if (!previa) return;

    setApagando(true);
    try {
      const r = await expurgarAssociado(previa.associado.id, dados);
      setResultado(r);
      setPrevia(null);
      setModalAberto(false);
      setCpf('');
      carregarHistorico();
    } catch (erro: any) {
      // Falha em VOZ ALTA: é escrita destrutiva pedida pelo usuário, e o modal
      // continua aberto para ele ver o motivo sem perder o que digitou.
      toast.error(erro?.message ?? 'Não foi possível apagar este cadastro.');
    } finally {
      setApagando(false);
    }
  };

  const liberar = async (registro: RegistroExpurgo) => {
    try {
      await liberarDaLista(registro.id);
      toast.success(
        `${formatCpfCnpj(registro.cpfCnpj)} liberado — volta a poder ser importado do SGA.`,
      );
      carregarHistorico();
    } catch (erro: any) {
      toast.error(erro?.message ?? 'Não foi possível liberar este CPF.');
    }
  };

  return (
    <ShellSaac
      cabecalho={<PageHeader titulo="Excluir Associado" aoVoltar={voltar} />}
    >
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-4xl tw-flex-col tw-gap-6 tw-px-4 tw-pb-10 tw-pt-4 lg:tw-px-8">
        <Nota variante="atencao">
          <strong>Esta é a única ação do sistema que apaga de verdade.</strong> Todo o
          resto do SAAC apenas marca o registro como excluído — e o cadastro marcado
          continua ocupando o CPF, o RG e a placa, que são únicos. Use isto para
          limpar cadastro de teste; não há como desfazer.
        </Nota>

        {/* ------------------------------------------------ busca por CPF */}
        <Card>
          <form onSubmit={buscar} className="tw-flex tw-flex-col tw-gap-3">
            <Field
              rotulo="CPF ou CNPJ do associado"
              dica="Digite os números; a busca ignora pontuação."
              erro={erroBusca ?? ''}
              campo="cpf"
            >
              <Input
                value={cpf}
                inputMode="numeric"
                placeholder="000.000.000-00"
                disabled={buscando}
                onChange={(evento) => setCpf(evento.target.value)}
              />
            </Field>

            <div>
              <Botao type="submit" carregando={buscando} iconeEsquerda="busca">
                Buscar cadastro
              </Botao>
            </div>
          </form>
        </Card>

        {buscando && <Skeleton linhas={4} />}

        {/* ------------------------------------------- resultado da última exclusão */}
        {resultado && (
          <Nota>
            <strong>Pronto.</strong> {resultado.mensagem}
            {resultado.arquivos.comFalha.length > 0 && (
              <ul className="tw-mb-0 tw-mt-2 tw-pl-5">
                {resultado.arquivos.comFalha.map((caminho) => (
                  <li key={caminho} className="tw-text-xs tw-text-texto-suave">
                    {caminho}
                  </li>
                ))}
              </ul>
            )}
          </Nota>
        )}

        {/* ------------------------------------------------------ a prévia */}
        {previa && (
          <Card>
            <div className="tw-flex tw-flex-col tw-gap-4">
              <div>
                <h2 className="tw-m-0 tw-font-marca tw-text-lg tw-font-bold tw-text-texto-titulo">
                  {previa.associado.nomeCompleto}
                </h2>
                <p className="tw-m-0 tw-text-sm tw-text-texto-suave">
                  {formatCpfCnpj(previa.associado.cpfCnpj)} · consultor{' '}
                  {previa.associado.volunteerCode} · cadastrado em{' '}
                  {formatarDataHora(previa.associado.criadoEm)}
                </p>
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
                  <Chip variante={previa.noSga ? 'pendente' : 'neutro'}>
                    {previa.noSga
                      ? `No SGA — código ${previa.associado.codigoAssociado}`
                      : 'Nunca subiu ao SGA'}
                  </Chip>
                  <Chip variante="neutro">
                    {previa.associado.origem === 'SGA_IMPORTADO'
                      ? 'Importado do SGA'
                      : 'Criado no SAAC'}
                  </Chip>
                </div>
              </div>

              {previa.avisos.map((aviso) => (
                <Nota key={aviso} variante="atencao">
                  {aviso}
                </Nota>
              ))}

              {previa.veiculos.length > 0 && (
                <div>
                  <h3 className="tw-mb-2 tw-mt-0 tw-text-sm tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-suave">
                    Veículos
                  </h3>
                  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
                    {previa.veiculos.map((veiculo) => (
                      <li
                        key={veiculo.id}
                        className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm"
                      >
                        <strong>{veiculo.placa}</strong>
                        <span className="tw-text-texto-suave">{veiculo.modelo}</span>
                        {veiculo.situacao === 'SUBSTITUIDO' && (
                          <Chip variante="neutro">Substituído</Chip>
                        )}
                        {veiculo.excluido && <Chip variante="neutro">Excluído</Chip>}
                        {veiculo.codigoVeiculo && (
                          <Chip variante="pendente">No SGA</Chip>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="tw-mb-2 tw-mt-0 tw-text-sm tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-suave">
                  O que será apagado
                </h3>
                <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1 tw-p-0 sm:tw-grid-cols-3">
                  {LINHAS_DO_INVENTARIO.filter(
                    ({ chave }) => (previa.inventario[chave] ?? 0) > 0,
                  ).map(({ chave, rotulo }) => (
                    <li key={chave} className="tw-text-sm">
                      <strong>{previa.inventario[chave]}</strong>{' '}
                      <span className="tw-text-texto-suave">{rotulo}</span>
                    </li>
                  ))}
                </ul>
                <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
                  O histórico de envios ao SGA é preservado — é a trilha do que já foi
                  mandado à Hinova.
                </p>
              </div>

              <div>
                {/*
                  ⚠️ O texto é DIFERENTE do botão do modal ("Apagar em definitivo"),
                  de propósito: este abre a confirmação, aquele executa. Dois botões
                  com o mesmo rótulo num fluxo irreversível é convite a confundir
                  qual dos dois já foi clicado — e é o padrão do `ModalDistrato`
                  ("Cancelar" na tela, "Cancelar contrato" no modal).
                */}
                <Botao variante="perigo" onClick={() => setModalAberto(true)}>
                  Excluir este cadastro
                </Botao>
              </div>
            </div>
          </Card>
        )}

        {/* --------------------------------------------------- histórico */}
        <section>
          <h2 className="tw-mb-2 tw-mt-0 tw-font-marca tw-text-sm tw-font-bold tw-uppercase tw-tracking-wider tw-text-texto-suave">
            Exclusões já feitas
          </h2>

          {carregandoHistorico && <Skeleton linhas={2} />}

          {!carregandoHistorico && erroHistorico && (
            <ErrorState
              mensagem="Não foi possível carregar o histórico de exclusões."
              detalhe={erroHistorico}
              aoTentarNovamente={carregarHistorico}
            />
          )}

          {!carregandoHistorico && !erroHistorico && historico?.itens.length === 0 && (
            <EmptyState
              titulo="Nenhum cadastro foi apagado até agora"
              descricao="Quando um associado for excluído em definitivo, ele aparece aqui — e este CPF fica bloqueado para reimportação automática do SGA."
              icone="sem-dados"
            />
          )}

          {!carregandoHistorico && !erroHistorico && !!historico?.itens.length && (
            <DataList
              rotuloAcessivel="Exclusões definitivas já realizadas"
              itens={historico.itens}
              chave={(registro) => registro.id}
              colunas={[
                {
                  id: 'nome',
                  rotulo: 'Associado',
                  principal: true,
                  celula: (registro) => registro.nomeCompleto,
                },
                {
                  id: 'cpf',
                  rotulo: 'CPF',
                  secundaria: true,
                  celula: (registro) => formatCpfCnpj(registro.cpfCnpj),
                },
                {
                  id: 'quando',
                  rotulo: 'Quando',
                  secundaria: true,
                  celula: (registro) => formatarDataHora(registro.expurgadoEm),
                },
                {
                  id: 'quem',
                  rotulo: 'Por',
                  celula: (registro) => registro.expurgadoPorNome ?? '—',
                },
                {
                  id: 'motivo',
                  rotulo: 'Motivo',
                  celula: (registro) => registro.motivo,
                },
                {
                  id: 'sga',
                  rotulo: 'SGA',
                  celula: (registro) =>
                    registro.marcadoTesteNoSga ? (
                      <Chip variante="neutro">Marcado como teste</Chip>
                    ) : (
                      '—'
                    ),
                },
                {
                  id: 'arquivos',
                  rotulo: 'Arquivos',
                  celula: (registro) =>
                    registro.arquivosComFalha.length > 0 ? (
                      <Chip variante="pendente">
                        {registro.arquivosComFalha.length} ficaram para trás
                      </Chip>
                    ) : (
                      `${registro.arquivosRemovidos} removidos`
                    ),
                },
              ]}
              acoes={(registro) => (
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  onClick={() => liberar(registro)}
                >
                  Liberar reimportação
                </Botao>
              )}
              rotuloAcoes="Ações"
            />
          )}

          <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
            Enquanto o CPF estiver nesta lista, a sincronização automática com o SGA
            não traz o cadastro de volta. Liberar desfaz só esse bloqueio — o registro
            apagado não volta sozinho.
          </p>
        </section>
      </div>

      <ModalConfirmarExpurgo
        aberto={modalAberto}
        previa={previa}
        processando={apagando}
        aoConfirmar={confirmarExpurgo}
        aoCancelar={() => setModalAberto(false)}
      />
    </ShellSaac>
  );
};

export default ExpurgoAssociadoPage;
